import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import LottieAnimation from '../LottieAnimation';
import ballData from '../../assets/lottie/pr-ball-white.json';
import { GEOMETRY, fullscreenGeometry, roomFrame } from './mirrorRoomMath';

/**
 * The mirror room: the surface opens to black from a horizontal slit, the ball
 * hangs in it throwing tinted light, `[ INNER CIRCLE ]` types out over
 * `{NAME} WILL KNOW.`, then it closes. Mount it only while it plays
 * (`q != null`). The Lottie is ~428 KB of work per instance and a grid holds
 * many cards, so nothing is mounted at rest.
 *
 * Its parent must be `position: relative; overflow: hidden`. A card's mark sits
 * above it (z-2); a row's mark sits under it and the ball takes its place.
 */
export default function MirrorRoom({ q, ...rest }) {
  if (q == null) return null;
  return <Room q={q} {...rest} />;
}

/**
 * `anchorRef` (optional): the mark's element. Surfaces where the mark isn't a
 * fixed distance from the right edge (a wrapping header, rows with their own
 * padding) pass it, and the ball and the spots' origin are measured onto the
 * mark instead of taken from the geometry table.
 */
function Room({ q, surface, first, anchorRef }) {
  // The full-screen room (a friend's profile) covers the viewport from
  // document.body, above the tab bar and the anthem, instead of a card.
  const g0 = useMemo(() => (surface === 'fullscreen'
    ? fullscreenGeometry(window.innerWidth, window.innerHeight)
    : GEOMETRY[surface] || GEOMETRY.friends), [surface]);
  const overlayRef = useRef(null);
  const [anchor, setAnchor] = useState(null);
  useLayoutEffect(() => {
    const mark = anchorRef?.current;
    const box = overlayRef.current;
    if (!mark || !box) return;
    const m = mark.getBoundingClientRect();
    const b = box.getBoundingClientRect();
    if (!b.width) return;
    const x = m.left + m.width / 2 - b.left;
    setAnchor({ x, y: m.top + m.height / 2 - b.top, right: b.width - x });
  }, [anchorRef]);
  const g = anchor
    ? { ...g0, bx: `${anchor.x.toFixed(1)}px`, by: `${anchor.y.toFixed(1)}px`, mr: `${anchor.right.toFixed(1)}px` }
    : g0;
  const f = roomFrame(q, g);
  const fr = g.label >= 20 ? 3 : 2; // fringe offset grows with the type
  const text = { fontSize: g.label, fontWeight: 700, letterSpacing: '0.18em', whiteSpace: 'pre', lineHeight: 1.2 };

  const room = (
    <div
      ref={overlayRef}
      aria-hidden="true"
      data-testid="mirror-room"
      className="pr-overlay-root font-mono"
      style={{
        position: g.fixed ? 'fixed' : 'absolute',
        inset: g.inset || 0,
        zIndex: g.fixed ? 100 : 1,
        background: '#000',
        overflow: 'hidden',
        pointerEvents: 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: g.justify,
        gap: 3,
        padding: g.pad,
        clipPath: f.clip,
        WebkitClipPath: f.clip,
      }}
    >
      {f.beams.map((b) => (
        <div
          key={b.color}
          style={{
            position: 'absolute', left: g.bx, top: g.by || '50%', height: g.bh || 18, marginTop: -(g.bh || 18) / 2, width: b.width,
            transformOrigin: '0 50%', transform: `rotate(${b.rotate.toFixed(1)}deg)`, background: b.color, opacity: b.opacity,
          }}
        />
      ))}
      {f.spots.map((s) => (
        <div
          key={s.y}
          style={{
            position: 'absolute', top: `${s.y}%`, marginTop: -s.h / 2, right: `calc(${g.mr} + ${(s.d * 100).toFixed(2)}%)`,
            width: s.w, height: s.h, background: s.color, opacity: s.opacity,
          }}
        />
      ))}

      <div
        style={{
          position: 'absolute', left: g.bx, top: g.by || '50%', width: g.ball, height: g.ball,
          marginLeft: -g.ball / 2, marginTop: -g.ball / 2,
          opacity: f.ballOpacity, transform: `scale(${f.ballScale.toFixed(3)})`,
        }}
      >
        <LottieAnimation animationData={ballData} width="100%" height="100%" preserveAspectRatio="xMidYMid meet" loop className="h-full w-full" />
      </div>

      <span style={{ position: 'relative', display: 'inline-block' }}>
        <span style={{ ...text, position: 'absolute', top: 0, left: fr * f.fringe, color: '#FF00FF', opacity: 0.9 * f.fringe }}>{f.label}</span>
        <span style={{ ...text, position: 'absolute', top: 0, left: -fr * f.fringe, color: '#00FFFF', opacity: 0.9 * f.fringe }}>{f.label}</span>
        <span style={{ ...text, position: 'relative', color: '#FFFFFF' }}>{f.label}</span>
      </span>
      <span style={{ position: 'relative', fontSize: g.sub || 10, letterSpacing: '0.12em', color: '#9CA3AF', opacity: f.subOpacity }}>
        {first} WILL KNOW.
      </span>
    </div>
  );
  return g.fixed ? createPortal(room, document.body) : room;
}
